'use client';

/**
 * Live insets for the "knowledge-map" reference screen (canvas 1672×941).
 *
 * Right drawer (x ≥ 1252) is live for `selectedGraphNode` / `graphDrawerOpen`;
 * the mode segment and the five layer switches on the left are live controls;
 * a halo marks the selected node and a veil dims a row whose layer is off.
 * The graph itself, its edges and node labels stay painted.
 */

import { Fragment, useRef, type CSSProperties } from 'react';
import type { ReferenceDemoState } from '../../../reference/types';
import { rectStyle, SCENE_CANVAS, type Rect, type SceneCanvas } from '../../types';
import type { InsetProps } from '../index';
import styles from './knowledge-map.module.css';

const C = SCENE_CANVAS['knowledge-map'];

type LayerKey = keyof ReferenceDemoState['graphLayers'];
type NodeType = 'concept' | 'prerequisite' | 'method' | 'error' | 'example';
type Dot = 'm' | 'w' | 'u';

const DRAWER: Rect = [1252, 0, 420, 941];
const DRAWER_CANVAS: SceneCanvas = { width: DRAWER[2], height: DRAWER[3] };
/** Position a block inside the drawer (rect in canvas px). */
const inDrawer = (rect: Rect) => rectStyle([rect[0] - DRAWER[0], rect[1] - DRAWER[1], rect[2], rect[3]], DRAWER_CANVAS);

const MODE: Rect = [27, 218, 224, 36];
const LAYER_ROW_X = 25;
const LAYER_ROW_W = 229;
const LAYER_ROW_H = 42;
const LAYER_ROW_Y = (i: number) => 324 + i * 48;
/** Measured centres of the painted switches (they are not on an exact 48 px grid). */
const SWITCH_CY = [341, 391, 440, 485, 536] as const;
const SWITCH_W = 36;
const SWITCH_H = 18;

const LAYERS: readonly { key: LayerKey; ru: string; en: string; veil: Rect }[] = [
  { key: 'concepts', ru: 'Концепции', en: 'Concepts', veil: [330, 40, 870, 255] },
  { key: 'prerequisites', ru: 'Предпосылки', en: 'Prerequisites', veil: [330, 330, 870, 115] },
  { key: 'methods', ru: 'Методы решения', en: 'Solution methods', veil: [330, 470, 870, 120] },
  { key: 'errors', ru: 'Типичные ошибки', en: 'Typical errors', veil: [330, 600, 870, 130] },
  { key: 'examples', ru: 'Примеры', en: 'Examples', veil: [330, 755, 870, 140] },
];

/** Node hit rects from reference/hotspots.ts. */
const NODE_RECTS: Record<string, Rect> = {
  'linear-equations': [692, 51, 110, 80], inequalities: [980, 86, 110, 80], 'quadratic-equations': [675, 157, 148, 102],
  arithmetic: [390, 347, 112, 82], monomials: [551, 348, 112, 82], 'short-multiplication': [690, 343, 116, 89], 'equality-properties': [850, 349, 112, 82], roots: [1035, 349, 112, 82],
  factorization: [427, 486, 121, 88], formula: [628, 485, 121, 88], vieta: [812, 486, 121, 88], 'graphical-method': [987, 487, 121, 88],
  'wrong-sign': [368, 616, 120, 88], 'lost-root': [507, 618, 120, 88], discriminant: [670, 613, 122, 92], 'bad-transform': [853, 620, 122, 88], 'ignored-domain': [1035, 618, 122, 88],
  'example-1': [347, 772, 124, 99], 'example-2': [508, 772, 124, 99], 'example-3': [676, 770, 124, 105], 'example-4': [863, 773, 124, 99], 'example-5': [1040, 773, 124, 99],
};

interface NodeCopy {
  title: { ru: string; en: string };
  glyph: string;
  type: NodeType;
  summary: { ru: string; en: string };
  next: { ru: string; en: string };
  /** Four relation rows × five qualitative markers (mastered / weak / unknown), as the legend paints them. */
  dots: readonly [string, string, string, string];
}

const TYPE_LABEL: Record<NodeType, { ru: string; en: string }> = {
  concept: { ru: 'Концепция', en: 'Concept' },
  prerequisite: { ru: 'Предпосылка', en: 'Prerequisite' },
  method: { ru: 'Метод решения', en: 'Solution method' },
  error: { ru: 'Типичная ошибка', en: 'Typical error' },
  example: { ru: 'Пример', en: 'Example' },
};

const NODES: Record<string, NodeCopy> = {
  'linear-equations': { title: { ru: 'Линейные уравнения', en: 'Linear equations' }, glyph: 'ax', type: 'concept', summary: { ru: 'Уравнения вида ax + b = 0. Основа для решения более сложных уравнений и систем.', en: 'Equations of the form ax + b = 0. The base for solving harder equations and systems.' }, next: { ru: 'Проверьте перенос слагаемых и деление на коэффициент, затем переходите к квадратным уравнениям.', en: 'Check moving terms and dividing by the coefficient, then move on to quadratic equations.' }, dots: ['mmmmw', 'mmmmu', 'mmmuu', 'mmmmw'] },
  inequalities: { title: { ru: 'Неравенства', en: 'Inequalities' }, glyph: '>', type: 'concept', summary: { ru: 'Сравнение выражений со знаками <, >, ≤, ≥. Решение записывается промежутком.', en: 'Comparing expressions with <, >, ≤, ≥. The solution is written as an interval.' }, next: { ru: 'Отработайте смену знака при умножении на отрицательное число и запись ответа промежутком.', en: 'Practise flipping the sign when multiplying by a negative and writing the answer as an interval.' }, dots: ['mmwwu', 'mmwuu', 'mwuuu', 'mmwuu'] },
  'quadratic-equations': { title: { ru: 'Квадратные уравнения', en: 'Quadratic equations' }, glyph: 'x²', type: 'concept', summary: { ru: 'Уравнения вида ax² + bx + c = 0, где a ≠ 0. Играют ключевую роль в алгебре и приложениях.', en: 'Equations of the form ax² + bx + c = 0 with a ≠ 0. They play a key role in algebra and its applications.' }, next: { ru: 'Закрепите формулу квадратного уравнения и потренируйтесь в решении уравнений с параметрами.', en: 'Consolidate the quadratic formula and practise solving equations with parameters.' }, dots: ['mmmwu', 'mmmwu', 'mmuuu', 'mmmwu'] },
  arithmetic: { title: { ru: 'Арифметика и дроби', en: 'Arithmetic and fractions' }, glyph: '½', type: 'prerequisite', summary: { ru: 'Действия с числами и дробями. Без них ошибки появляются в любом уравнении.', en: 'Operations with numbers and fractions. Without them errors appear in any equation.' }, next: { ru: 'Повторите сложение дробей с разными знаменателями и знаки при вычитании.', en: 'Review adding fractions with different denominators and signs in subtraction.' }, dots: ['mmmmm', 'mmmmw', 'mmmuu', 'mmmmm'] },
  monomials: { title: { ru: 'Одночлены и многочлены', en: 'Monomials and polynomials' }, glyph: 'xⁿ', type: 'prerequisite', summary: { ru: 'Приведение подобных, раскрытие скобок, степень многочлена.', en: 'Combining like terms, expanding brackets, the degree of a polynomial.' }, next: { ru: 'Потренируйте раскрытие скобок со знаком минус перед ними.', en: 'Practise expanding brackets with a minus sign in front.' }, dots: ['mmmwu', 'mmmwu', 'mmwuu', 'mmmwu'] },
  'short-multiplication': { title: { ru: 'Формулы сокращённого умножения', en: 'Special products' }, glyph: '(a+b)²', type: 'prerequisite', summary: { ru: 'Квадрат суммы и разности, разность квадратов. Ускоряют разложение на множители.', en: 'Square of a sum and a difference, difference of squares. They speed up factoring.' }, next: { ru: 'Узнайте формулу в выражении: три примера на разность квадратов.', en: 'Recognise the formula in an expression: three difference of squares examples.' }, dots: ['mmwwu', 'mmwuu', 'mwuuu', 'mmwwu'] },
  'equality-properties': { title: { ru: 'Свойства равенств', en: 'Properties of equality' }, glyph: '=', type: 'prerequisite', summary: { ru: 'Что можно делать с обеими частями уравнения, не меняя его корней.', en: 'What can be done to both sides of an equation without changing its roots.' }, next: { ru: 'Проверьте деление обеих частей на выражение с переменной.', en: 'Check dividing both sides by an expression that contains the variable.' }, dots: ['mmmmw', 'mmmwu', 'mmwuu', 'mmmmw'] },
  roots: { title: { ru: 'Степени и корни', en: 'Powers and roots' }, glyph: '√', type: 'prerequisite', summary: { ru: 'Квадратный корень, свойства степеней, арифметический корень.', en: 'Square root, properties of powers, the principal root.' }, next: { ru: 'Отработайте извлечение корня из квадрата: два знака, а не один.', en: 'Practise taking the root of a square: two signs, not one.' }, dots: ['mmwuu', 'mwuuu', 'mwuuu', 'mmwuu'] },
  factorization: { title: { ru: 'Разложение на множители', en: 'Factoring' }, glyph: '(x−a)', type: 'method', summary: { ru: 'Уравнение сводится к произведению множителей, равному нулю.', en: 'The equation is reduced to a product of factors equal to zero.' }, next: { ru: 'Три уравнения, где разложение короче формулы.', en: 'Three equations where factoring is shorter than the formula.' }, dots: ['mmmwu', 'mmwuu', 'mmuuu', 'mmmwu'] },
  formula: { title: { ru: 'Формула квадратного уравнения', en: 'Quadratic formula' }, glyph: 'D', type: 'method', summary: { ru: 'Корни через дискриминант: x = (−b ± √D) / 2a. Работает для любого квадратного уравнения.', en: 'Roots via the discriminant: x = (−b ± √D) / 2a. Works for any quadratic equation.' }, next: { ru: 'Закрепите вычисление D со знаками и случай D = 0.', en: 'Consolidate computing D with signs and the case D = 0.' }, dots: ['mmmmw', 'mmmwu', 'mmwuu', 'mmmmw'] },
  vieta: { title: { ru: 'Теорема Виета', en: 'Vieta’s theorem' }, glyph: 'x₁x₂', type: 'method', summary: { ru: 'Сумма корней равна −b/a, произведение равно c/a. Быстрая проверка и подбор корней.', en: 'The sum of the roots is −b/a, the product is c/a. A quick check and a way to guess roots.' }, next: { ru: 'Подберите корни устно для пяти приведённых уравнений и проверьте по формуле.', en: 'Guess the roots of five monic equations aloud and check with the formula.' }, dots: ['mmwwu', 'mwuuu', 'mwuuu', 'mmwuu'] },
  'graphical-method': { title: { ru: 'Графический метод', en: 'Graphical method' }, glyph: '∪', type: 'method', summary: { ru: 'Корни как точки пересечения параболы с осью x.', en: 'Roots as the points where the parabola meets the x axis.' }, next: { ru: 'Постройте эскиз параболы по знаку a и вершине, оцените число корней.', en: 'Sketch the parabola from the sign of a and the vertex, estimate the number of roots.' }, dots: ['mmwuu', 'mmwuu', 'mwuuu', 'mmwuu'] },
  'wrong-sign': { title: { ru: 'Неверный знак', en: 'Wrong sign' }, glyph: '±', type: 'error', summary: { ru: 'Знак теряется при переносе слагаемого или в формуле корней.', en: 'A sign is lost when moving a term or inside the root formula.' }, next: { ru: 'Два примера с проверкой подстановкой: где именно меняется знак.', en: 'Two examples checked by substitution: where exactly the sign changes.' }, dots: ['mmmuu', 'mmwuu', 'wwuuu', 'mmwuu'] },
  'lost-root': { title: { ru: 'Потеря корня при делении', en: 'Lost root when dividing' }, glyph: 'x=0', type: 'error', summary: { ru: 'Деление обеих частей на выражение с x отбрасывает корень x = 0.', en: 'Dividing both sides by an expression with x drops the root x = 0.' }, next: { ru: 'Вынесите общий множитель вместо деления: три уравнения.', en: 'Factor out the common term instead of dividing: three equations.' }, dots: ['mmwuu', 'mwuuu', 'wuuuu', 'mwuuu'] },
  discriminant: { title: { ru: 'Ошибки в дискриминанте', en: 'Discriminant errors' }, glyph: 'D<0', type: 'error', summary: { ru: 'Неверный знак b² или 4ac, забытый случай D < 0.', en: 'A wrong sign on b² or 4ac, or the forgotten case D < 0.' }, next: { ru: 'Вычислите D для четырёх уравнений и назовите число корней до решения.', en: 'Compute D for four equations and state the number of roots before solving.' }, dots: ['mmwuu', 'mmwuu', 'wwuuu', 'mmwuu'] },
  'bad-transform': { title: { ru: 'Неверное преобразование', en: 'Wrong transformation' }, glyph: '≠', type: 'error', summary: { ru: 'Скобки раскрыты с ошибкой или подобные приведены неверно.', en: 'Brackets expanded incorrectly or like terms combined wrongly.' }, next: { ru: 'Проверка каждого шага подстановкой числа: один пример.', en: 'Check every step by substituting a number: one example.' }, dots: ['mmwuu', 'mwuuu', 'wwuuu', 'mwuuu'] },
  'ignored-domain': { title: { ru: 'Игнорирование ОДЗ', en: 'Ignored domain' }, glyph: 'x≠', type: 'error', summary: { ru: 'Найденный корень не проверен на допустимость.', en: 'A found root is not checked against the domain.' }, next: { ru: 'Уравнения со знаменателем: сначала ОДЗ, потом корни.', en: 'Equations with a denominator: the domain first, then the roots.' }, dots: ['mwuuu', 'mwuuu', 'wuuuu', 'mwuuu'] },
  'example-1': { title: { ru: 'ax² + bx + c = 0', en: 'ax² + bx + c = 0' }, glyph: '1', type: 'example', summary: { ru: 'Общий вид: назовите a, b, c и вычислите дискриминант.', en: 'The general form: name a, b, c and compute the discriminant.' }, next: { ru: 'Решите два уравнения общего вида по формуле.', en: 'Solve two general form equations with the formula.' }, dots: ['mmmwu', 'mmmwu', 'mmuuu', 'mmmmw'] },
  'example-2': { title: { ru: 'x² + px + q = 0', en: 'x² + px + q = 0' }, glyph: '2', type: 'example', summary: { ru: 'Приведённое уравнение: удобно для теоремы Виета.', en: 'A monic equation: convenient for Vieta’s theorem.' }, next: { ru: 'Подберите корни устно, проверьте сумму и произведение.', en: 'Guess the roots aloud, check the sum and the product.' }, dots: ['mmwuu', 'mwuuu', 'mwuuu', 'mmwuu'] },
  'example-3': { title: { ru: '2x² − 5x + 2 = 0', en: '2x² − 5x + 2 = 0' }, glyph: '3', type: 'example', summary: { ru: 'Уравнение с двумя корнями: подходит и формула, и разложение.', en: 'An equation with two roots: both the formula and factoring work.' }, next: { ru: 'Решите двумя способами и сравните число шагов.', en: 'Solve it both ways and compare the number of steps.' }, dots: ['mmmwu', 'mmmwu', 'mmwuu', 'mmmwu'] },
  'example-4': { title: { ru: '(x + 1)² = 3', en: '(x + 1)² = 3' }, glyph: '4', type: 'example', summary: { ru: 'Уравнение через квадрат: корень извлекается с двумя знаками.', en: 'An equation via a square: the root comes with two signs.' }, next: { ru: 'Три уравнения вида (x + m)² = n без раскрытия скобок.', en: 'Three equations of the form (x + m)² = n without expanding.' }, dots: ['mmwuu', 'mmwuu', 'mwuuu', 'mmwuu'] },
  'example-5': { title: { ru: 'x² + 4x + 4 < 0', en: 'x² + 4x + 4 < 0' }, glyph: '5', type: 'example', summary: { ru: 'Неравенство с полным квадратом: решений нет, левая часть ≥ 0.', en: 'An inequality with a perfect square: no solutions, the left side is ≥ 0.' }, next: { ru: 'Оцените знак выражения до решения: два неравенства.', en: 'Estimate the sign of the expression before solving: two inequalities.' }, dots: ['mwuuu', 'mwuuu', 'wuuuu', 'mwuuu'] },
};
const FALLBACK_NODE = 'quadratic-equations';

const RELATIONS = [
  { ru: 'Предпосылки', en: 'Prerequisites' },
  { ru: 'Методы решения', en: 'Solution methods' },
  { ru: 'Типичные ошибки', en: 'Typical errors' },
  { ru: 'Примеры', en: 'Examples' },
] as const;
const RELATED = [
  { ru: 'Квадратичные функции', en: 'Quadratic functions' },
  { ru: 'Неравенства второй степени', en: 'Quadratic inequalities' },
  { ru: 'Системы уравнений', en: 'Systems of equations' },
] as const;

const T = {
  mode: { ru: 'Режим карты', en: 'Map mode' },
  teacher: { ru: 'Учитель', en: 'Teacher' },
  student: { ru: 'Ученик', en: 'Student' },
  teacherMode: { ru: 'Режим учителя', en: 'Teacher mode' },
  studentMode: { ru: 'Режим ученика', en: 'Student mode' },
  layers: { ru: 'Слои карты', en: 'Map layers' },
  layer: { ru: 'Слой', en: 'Layer' },
  links: { ru: 'Связи и освоение', en: 'Links and mastery' },
  link: { ru: 'Связь', en: 'Relationship' },
  explain: { ru: 'Объясни связь', en: 'Explain relationship' },
  next: { ru: 'Что дальше', en: 'What next' },
  cta: { ru: 'Подобрать задания', en: 'Select exercises' },
  related: { ru: 'Связанные концепции', en: 'Related concepts' },
  relatedOne: { ru: 'Связанная концепция', en: 'Related concept' },
  showRelated: { ru: 'Покажи связь с концепцией', en: 'Show the connection to' },
  showAll: { ru: 'Показать все', en: 'Show all' },
  showAllSeed: { ru: 'Покажи все связанные концепции', en: 'Show all related concepts' },
  close: { ru: 'Закрыть карточку узла', en: 'Close node details' },
  closed: { ru: 'Карточка узла закрыта', en: 'Node details closed' },
  pickNode: { ru: 'Выберите узел на карте, чтобы открыть его карточку', en: 'Pick a node on the map to open its details' },
  node: { ru: 'Узел карты', en: 'Map node' },
  selectedNode: { ru: 'Выбранный узел', en: 'Selected node' },
  hidden: { ru: 'слой скрыт', en: 'layer hidden' },
} as const;

function useChanged(value: unknown): boolean {
  const initial = useRef(value);
  const changed = useRef(false);
  if (!Object.is(value, initial.current)) changed.current = true;
  return changed.current;
}

function Chevron() {
  return (
    <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
      <path d="M4 2.5 7.5 6 4 9.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function KnowledgeMapInsets({ lang, state, update, navigate, openAssistant, spotlight, onInteraction }: InsetProps) {
  const pick = <V,>(pair: { ru: V; en: V }): V => (lang === 'ru' ? pair.ru : pair.en);
  const nodeId = state.selectedGraphNode in NODES ? state.selectedGraphNode : FALLBACK_NODE;
  const node = NODES[nodeId];
  const nodeRect = NODE_RECTS[state.selectedGraphNode];
  const open = state.graphDrawerOpen;
  const live = useChanged(`${nodeId}|${open}`);
  const isSpot = (id: string) => spotlight === id;

  const act = (label: string, run: () => void) => () => {
    onInteraction?.(label);
    run();
  };

  const studentMode = state.graphMode === 'student';

  return (
    <div className={styles.root}>
      {/* ---------- mode segment ---------- */}
      <div className={styles.mode} style={rectStyle(MODE, C)} role="group" aria-label={pick(T.mode)}>
        <i className={styles.modeThumb} data-side={studentMode ? 'right' : 'left'} aria-hidden="true" />
        {([['teacher', T.teacher, T.teacherMode], ['student', T.student, T.studentMode]] as const).map(([mode, label, name]) => {
          const id = `map-${mode}`;
          const on = state.graphMode === mode;
          return (
            <button
              key={id}
              type="button"
              className={styles.modeButton}
              data-hotspot={id}
              data-on={on ? 'true' : undefined}
              data-spotlight={isSpot(id) ? 'true' : undefined}
              aria-pressed={on}
              aria-label={pick(name)}
              onClick={act(pick(name), () => update({ graphMode: mode }))}
            >
              {pick(label)}
              {isSpot(id) && <i className={styles.pulse} aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      {/* ---------- layer switches + row veils ---------- */}
      {LAYERS.map((layer, index) => {
        const id = `layer-${layer.key}`;
        const on = state.graphLayers[layer.key];
        const label = `${pick(T.layer)}: ${pick(layer)}`;
        const rowY = LAYER_ROW_Y(index);
        /** Switch geometry relative to the row button (percentages of 229×42). */
        const cy = SWITCH_CY[index] - rowY;
        const inRow = (rect: Rect) => rectStyle([rect[0] - LAYER_ROW_X, rect[1], rect[2], rect[3]], { width: LAYER_ROW_W, height: LAYER_ROW_H });
        return (
          <Fragment key={id}>
            <i className={styles.rowVeil} style={rectStyle(layer.veil, C)} data-on={on ? undefined : 'true'} aria-hidden="true" />
            <button
              type="button"
              role="switch"
              className={styles.layerRow}
              style={rectStyle([LAYER_ROW_X, rowY, LAYER_ROW_W, LAYER_ROW_H], C)}
              data-hotspot={id}
              data-spotlight={isSpot(id) ? 'true' : undefined}
              aria-checked={on}
              aria-label={label}
              onClick={act(label, () => update({ graphLayers: { [layer.key]: !on } }))}
            >
              <span className={styles.switchCover} style={inRow([207, cy - 12, 44, 24])} />
              <span
                className={styles.switch}
                style={inRow([211, cy - SWITCH_H / 2, SWITCH_W, SWITCH_H])}
                data-on={on ? 'true' : undefined}
              >
                <i />
              </span>
              {isSpot(id) && <i className={styles.pulse} aria-hidden="true" />}
            </button>
          </Fragment>
        );
      })}

      {/* ---------- halo over the selected node ---------- */}
      {nodeRect && (
        <i
          key={`halo-${state.selectedGraphNode}`}
          className={styles.halo}
          style={rectStyle(nodeRect, C)}
          data-live={live || undefined}
          role="img"
          aria-label={`${pick(T.selectedNode)}: ${pick(node.title)}`}
        />
      )}

      {/* ---------- node drawer ---------- */}
      <section className={styles.drawer} style={rectStyle(DRAWER, C)} data-live={live || undefined} data-open={open ? 'true' : undefined} aria-label={`${pick(T.node)}: ${pick(node.title)}`}>
        <p className={styles.empty} aria-hidden={open ? 'true' : undefined}>{pick(T.pickNode)}</p>

        <div className={styles.slide} aria-hidden={open ? undefined : 'true'}>
          <div key={nodeId} className={styles.body}>
            <button
              type="button"
              className={styles.close}
              style={inDrawer([1604, 13, 56, 55])}
              data-hotspot="map-close"
              data-spotlight={isSpot('map-close') ? 'true' : undefined}
              aria-label={pick(T.close)}
              tabIndex={open ? 0 : -1}
              onClick={act(pick(T.close), () => update({ graphDrawerOpen: false }))}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 3l10 10M13 3 3 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
              {isSpot('map-close') && <i className={styles.pulse} aria-hidden="true" />}
            </button>

            <span className={styles.glyph} style={{ ...inDrawer([1279, 63, 63, 63]), '--i': 0 } as CSSProperties} data-type={node.type} aria-hidden="true">{node.glyph}</span>
            <h3 className={styles.title} style={{ ...inDrawer([1364, 68, 300, 44]), '--i': 0 } as CSSProperties}>{pick(node.title)}</h3>
            <span className={styles.chip} style={{ ...inDrawer([1364, 103, 300, 24]), '--i': 1 } as CSSProperties}><b>{pick(TYPE_LABEL[node.type])}</b></span>
            <p className={styles.summary} style={{ ...inDrawer([1280, 152, 350, 90]), '--i': 1 } as CSSProperties}>{pick(node.summary)}</p>

            <i className={styles.rule} style={inDrawer([1280, 260, 360, 1])} aria-hidden="true" />
            <h4 className={styles.head} style={{ ...inDrawer([1280, 282, 300, 22]), '--i': 2 } as CSSProperties}>{pick(T.links)}</h4>
            {RELATIONS.map((relation, index) => {
              const id = `map-relation-${index}`;
              const label = `${pick(T.link)}: ${pick(relation)}`;
              return (
                <button
                  key={id}
                  type="button"
                  className={styles.row}
                  style={{ ...inDrawer([1274, 316 + index * 47, 366, 42]), '--i': Math.min(5, index + 2) } as CSSProperties}
                  data-hotspot={id}
                  data-spotlight={isSpot(id) ? 'true' : undefined}
                  aria-label={label}
                  tabIndex={open ? 0 : -1}
                  onClick={act(label, () => openAssistant(`${pick(T.explain)}: ${pick(relation)}`))}
                >
                  <span className={styles.rowIcon} aria-hidden="true">{['⤳', '⚙', '△', '▤'][index]}</span>
                  <span className={styles.rowLabel}>{pick(relation)}</span>
                  <span className={styles.dots} aria-hidden="true">
                    {node.dots[index].split('').map((dot, k) => <i key={k} data-dot={dot as Dot} />)}
                  </span>
                  <span className={styles.rowChevron} aria-hidden="true"><Chevron /></span>
                  {isSpot(id) && <i className={styles.pulse} aria-hidden="true" />}
                </button>
              );
            })}

            <i className={styles.rule} style={inDrawer([1280, 512, 360, 1])} aria-hidden="true" />
            <h4 className={styles.head} style={{ ...inDrawer([1280, 530, 300, 22]), '--i': 4 } as CSSProperties}>{pick(T.next)}</h4>
            <p className={styles.nextText} style={{ ...inDrawer([1280, 562, 350, 76]), '--i': 5 } as CSSProperties}>{pick(node.next)}</p>
            <button
              type="button"
              className={styles.cta}
              style={{ ...inDrawer([1280, 651, 225, 37]), '--i': 5 } as CSSProperties}
              data-hotspot="map-cta"
              data-spotlight={isSpot('map-cta') ? 'true' : undefined}
              aria-label={pick(T.cta)}
              tabIndex={open ? 0 : -1}
              onClick={act(pick(T.cta), () => navigate('personal-route'))}
            >
              <span aria-hidden="true">▶</span>
              {pick(T.cta)}
              {isSpot('map-cta') && <i className={styles.pulse} aria-hidden="true" />}
            </button>

            <i className={styles.rule} style={inDrawer([1280, 720, 360, 1])} aria-hidden="true" />
            <h4 className={styles.head} style={inDrawer([1280, 733, 300, 22])}>{pick(T.related)}</h4>
            {RELATED.map((item, index) => {
              const id = `map-related-${index}`;
              const label = `${pick(T.relatedOne)}: ${pick(item)}`;
              return (
                <button
                  key={id}
                  type="button"
                  className={styles.related}
                  style={inDrawer([1274, 763 + index * 35, 366, 33])}
                  data-hotspot={id}
                  data-spotlight={isSpot(id) ? 'true' : undefined}
                  aria-label={label}
                  tabIndex={open ? 0 : -1}
                  onClick={act(label, () => openAssistant(`${pick(T.showRelated)}: ${pick(item)}`))}
                >
                  <span className={styles.relatedIcon} aria-hidden="true">{['◔', '◱', '⋮⋮'][index]}</span>
                  <span className={styles.rowLabel}>{pick(item)}</span>
                  <span className={styles.bars} aria-hidden="true"><i /><i /><i /></span>
                  <span className={styles.rowChevron} aria-hidden="true"><Chevron /></span>
                  {isSpot(id) && <i className={styles.pulse} aria-hidden="true" />}
                </button>
              );
            })}
            <button
              type="button"
              className={styles.showAll}
              style={inDrawer([1280, 876, 160, 26])}
              aria-label={pick(T.showAll)}
              tabIndex={open ? 0 : -1}
              onClick={act(pick(T.showAll), () => openAssistant(pick(T.showAllSeed)))}
            >
              {pick(T.showAll)}
              <span aria-hidden="true"><Chevron /></span>
            </button>
          </div>
          <i key={`relight-${nodeId}`} className={styles.relight} aria-hidden="true" />
        </div>
      </section>
    </div>
  );
}
